import { Box, Checkbox, CircularProgress, Typography } from "@mui/material";
import { ShowAccordionSection } from "./ShowAccordionSection";
import { useState } from "react";
import { useNotify, useGetMany, FunctionField, Identifier } from "react-admin";
import { StatusChipField } from "./fields/StatusChipField";
import { Download as DownloadIcon } from "@mui/icons-material";
import { ButtonIconLoadingPosition, UIComponentSize } from "@/types/shared/uiComponents";
import Button from "../ui/Button";
import { StyledDatagrid } from "../ui/react-admin/StyledDatagrid";
import { AlertType, ResourceName, ValidAny } from "@/types";
import { formatFileSize, getFileExtension, triggerBlobDownload } from "@/utils/formatters/fileUtils";
import { composeDocumentLabel, composeDocumentLabelAria } from "@/utils/formatters/documentLabel";
import { getPartnerAwareApiUrl } from "@/providers/dataProvider/dataProvider";
import { fetchDocumentResource } from "@/providers/dataProvider/documentDownload";
import { useRichApiErrorNotify } from "@/hooks/useRichApiErrorNotify";

interface DocumentsTabProps {
  documentIds: Identifier[];
}

export const DocumentsTab = ({ documentIds }: DocumentsTabProps) => {
  if (!documentIds || documentIds.length === 0) {
    return (
      <Box flex={1} pt={4}>
        <ShowAccordionSection label="Documents" defaultExpanded>
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary">
              No documents found.
            </Typography>
          </Box>
        </ShowAccordionSection>
      </Box>
    );
  }

  return (
    <Box flex={1} pt={4}>
      <ShowAccordionSection label="Documents" defaultExpanded>
        <DocumentsTable documentIds={documentIds} />
      </ShowAccordionSection>
    </Box>
  );
};

/**
 * Fetches a document blob with proper auth headers.
 */
const fetchDocumentBlob = async (documentId: string): Promise<Blob> => {
  const url = `${getPartnerAwareApiUrl()}/documents/${documentId}/download`;
  // Partner-scoped, authenticated headers are built centrally so this raw download
  // can't silently drop X-Partner-ID (#5350).
  const response = await fetchDocumentResource(url);
  return response.blob();
};

const DownloadButton = ({ record }: { record: ValidAny }) => {
  const [downloading, setDownloading] = useState(false);
  const notifyError = useRichApiErrorNotify();

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const blob = await fetchDocumentBlob(record.id);
      triggerBlobDownload(blob, composeDocumentLabel(record));
    } catch (error) {
      notifyError(error);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Button
      size={UIComponentSize.Small}
      startIcon={<DownloadIcon />}
      loading={downloading}
      loadingPosition={ButtonIconLoadingPosition.Start}
      onClick={async (e) => {
        e.stopPropagation();
        await handleDownload();
      }}
    >
      {downloading ? "..." : "Download"}
    </Button>
  );
};

interface DocumentsTableProps {
  documentIds: Identifier[];
}

export const DocumentsTable = ({ documentIds }: DocumentsTableProps) => {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [downloading, setDownloading] = useState(false);
  const notify = useNotify();
  const notifyError = useRichApiErrorNotify();

  const { data: allDocuments = [], isLoading } = useGetMany(
    ResourceName.DOCUMENTS,
    { ids: documentIds || [] },
    { enabled: (documentIds?.length || 0) > 0 }
  );

  const handleSelectAll = () => {
    if (selectedIds.length === allDocuments.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(allDocuments.map((doc) => doc.id));
    }
  };

  const handleSelectOne = (id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((sid) => sid !== id) : [...prev, id]));
  };

  const handleBulkDownload = async () => {
    setDownloading(true);
    const selectedDocs = allDocuments.filter((doc) => selectedIds.includes(doc.id));

    for (const doc of selectedDocs) {
      try {
        const blob = await fetchDocumentBlob(doc.id);
        triggerBlobDownload(blob, composeDocumentLabel(doc));
        await new Promise((resolve) => setTimeout(resolve, 500));
      } catch (error) {
        notifyError(error);
      }
    }

    setDownloading(false);
    notify(`Downloaded ${selectedIds.length} documents`, { type: AlertType.SUCCESS });
    setSelectedIds([]);
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  if (allDocuments.length === 0) {
    return (
      <Box p={2}>
        <Typography variant="body2" color="text.secondary">
          No documents found.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {selectedIds.length > 0 && (
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
          p={2}
          bgcolor="action.hover"
          borderRadius={1}
        >
          <Typography variant="body2">
            {selectedIds.length} document{selectedIds.length !== 1 ? "s" : ""} selected
          </Typography>
          <Button
            startIcon={<DownloadIcon />}
            onClick={handleBulkDownload}
            loading={downloading}
            loadingPosition={ButtonIconLoadingPosition.Start}
          >
            {downloading ? "Downloading..." : "Download Selected"}
          </Button>
        </Box>
      )}

      <StyledDatagrid
        data={allDocuments}
        total={allDocuments.length}
        bulkActionButtons={false}
        rowClick={(id) => `/documents/${id}/show`}
      >
        <FunctionField
          label={
            <Checkbox
              checked={selectedIds.length === allDocuments.length && allDocuments.length > 0}
              indeterminate={selectedIds.length > 0 && selectedIds.length < allDocuments.length}
              onClick={(e) => {
                e.stopPropagation();
                handleSelectAll();
              }}
            />
          }
          render={(record) => (
            <Checkbox
              checked={selectedIds.includes(record.id)}
              onClick={(e) => {
                e.stopPropagation();
                handleSelectOne(record.id);
              }}
            />
          )}
        />

        <FunctionField
          label="Type"
          render={(record) => (
            <Typography variant="body2" sx={{ textTransform: "capitalize" }}>
              {record.type || "—"}
            </Typography>
          )}
        />

        <FunctionField label="File Type" render={(record) => getFileExtension(record.name)} />

        <FunctionField
          label="File Name"
          render={(record) => {
            const sheet = record.object_metadata?.sheet_name;
            // Em-dash is hidden from AT; aria-label reads "<name>, sheet <sheet_name>"
            // so screen readers don't pronounce the dash. MSW-8 AC8.5.
            return sheet ? (
              <Typography variant="body2" aria-label={composeDocumentLabelAria(record)}>
                {record.name}
                <span aria-hidden="true"> — </span>
                {sheet}
              </Typography>
            ) : (
              <Typography variant="body2">{composeDocumentLabel(record)}</Typography>
            );
          }}
        />

        <FunctionField label="File Size" render={(record) => formatFileSize(record.object_metadata?.content_length)} />

        <FunctionField
          label="Date Uploaded"
          render={(record) => {
            if (!record.uploaded_datetime) return "—";
            return new Date(record.uploaded_datetime).toLocaleDateString();
          }}
        />

        <StatusChipField source="status" />

        <FunctionField
          label="Actions"
          render={(record) => (
            <Box display="flex" gap={1}>
              <DownloadButton record={record} />
            </Box>
          )}
        />
      </StyledDatagrid>
    </Box>
  );
};
